<template>
    <el-dialog
        append-to-body
        :visible.sync="visible"
        :show-close="false"
        :modal="true"
        :close-on-click-modal="false"
        :close-on-press-escape="true"
        width="800px"
        custom-class="chat-modal-dialog"
        top="10vh"
        @close="$emit('close')"
    >
        <!-- 自定义头部 -->
        <div slot="title" class="chat-header-bar">
            <div class="chat-header-info">
                <img v-if="userInfo.userImageUrl" :src="userInfo.userImageUrl" alt="" class="chat-header-avatar">
                <div class="chat-header-text">
                    <h3>{{ userInfo.userName }}</h3>
                </div>
            </div>
            <button class="chat-close" @click="handleClose">
                <i class="el-icon-circle-close"></i>
            </button>
        </div>

        <div
            ref="messagesContainer"
            class="chat-messages"
            @scroll="handleScroll"
        >
            <div v-if="isLoading" class="chat-loading">
                <i class="el-icon-loading"></i>
                加载中...
            </div>
            <div
                v-for="message in messageList"
                :key="message.id"
                :class="['message-bubble', message.position === 'right' ? 'sent' : 'received']"
            >
                <div>{{ message.content }}</div>
                <div class="message-time">{{ getRelativeTime(message.sendTime) }}</div>
            </div>
        </div>

        <div slot="footer" class="chat-input-area">
            <input
                ref="messageInput"
                v-model="newMessage"
                class="chat-input"
                placeholder="输入消息..."
                type="text"
                @keyup.enter="sendMessage"
            >
            <button
                :class="{ 'disabled': !canSend, 'sending': isSending }"
                :disabled="!canSend || isSending"
                class="send-btn"
                @click="sendMessage"
            >
                <i :class="{ 'send-animation': isSending }" class="el-icon-s-promotion"></i>
            </button>
        </div>
    </el-dialog>
</template>

<script>
import Api from '@/components/Api/index.js'
import {getRelativeTime} from "../../util/util";

export default {
    name: 'ChatModal',
    props: {
        userInfo: {
            type: Object,
            default: () => ({})
        },
        show: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            chat: {},
            isSending: false,  // 发送状态
            messageList: [],
            newMessage: '',
            page: 1,
            size: 10,
            totalNumber: 0,
            isLoading: false,
            hasMore: true,
            isFirstLoad: true,  // 标记是否是首次加载
        }
    },
    computed: {
        // 同步 el-dialog 的显示状态
        visible: {
            get() {
                return this.show
            },
            set(val) {
                if (!val) {
                    this.$emit('close')
                }
            }
        },
        canSend() {
            return this.newMessage.trim().length > 0
        },
    },
    watch: {
        show(newVal) {
            if (newVal) {
                // 重置状态
                this.resetChat()
                // 加载初始数据
                this.loadInitialMessages()
                // 聚焦输入框
                this.$nextTick(() => {
                    this.$refs.messageInput && this.$refs.messageInput.focus()
                })
            }
        }
    },
    methods: {
        getRelativeTime,

        // 关闭弹窗
        handleClose() {
            this.visible = false
        },

        // 重置聊天状态
        resetChat() {
            this.messageList = []
            this.page = 1
            this.hasMore = true
            this.isFirstLoad = true
        },

        // 加载初始消息（最新的一页）
        loadInitialMessages() {
            this.isLoading = true
            return new Api().getChatList({
                size: this.size,
                page: 1,
                q: {userId: this.userInfo.userId},
            }).then(res => {
                if (res.status === 200) {
                    this.messageList = res.data.records.sort((a, b) => a.sendTime - b.sendTime) || []
                    this.totalNumber = res.data.totalNumber || res.data.total || 0
                    // 判断是否还有更多旧消息
                    this.hasMore = this.messageList.length < this.totalNumber

                    // 首次加载完成后滚动到底部
                    this.$nextTick(() => {
                        this.scrollToBottom()
                        this.isFirstLoad = false
                    })
                }
                return res
            }).finally(() => {
                this.isLoading = false
            })
        },

        // 发送消息
        sendMessage() {
            if (!this.newMessage.trim() || this.isSending) return;

            this.isSending = true;  // 开始发送动画

            let datas = {}
            if (this.messageList.length) {
                datas = this.messageList[0];
            }
            const message = {
                keys: datas.keys || this.userInfo.userId + "-" + this.$store.state.userInfor.userId,
                type: 0,
                content: this.newMessage,
                chatType: 0,
            };

            // 清空输入框
            this.newMessage = '';

            new Api("user")
                .sendChat(message)
                .then((res) => {
                    if (res.status === 200) {
                        // 成功发送后，刷新当前聊天记录
                        this.loadInitialMessages()
                        this.$nextTick(() => {
                            this.scrollToBottom()
                        })
                    } else {
                        this.$message.error(res.statusText);
                    }
                })
                .catch((err) => {
                    this.$message.error("发送失败");
                })
                .finally(() => {
                    // 动画持续300ms后恢复
                    setTimeout(() => {
                        this.isSending = false;
                    }, 300);
                });
        },

        // 容器滚动监听：靠近顶部时加载更多旧消息
        handleScroll() {
            const container = this.$refs.messagesContainer
            if (!container || this.isLoading || !this.hasMore || this.isFirstLoad) {
                return
            }

            // 当滚动到距离顶部50px以内时，触发加载
            if (container.scrollTop <= 50) {
                this.loadOlderMessages()
            }
        },

        // 加载更旧的消息
        loadOlderMessages() {
            if (this.isLoading || !this.hasMore) {
                return Promise.resolve()
            }

            const container = this.$refs.messagesContainer
            if (!container) return Promise.resolve()

            // 记录当前滚动位置和高度
            const prevScrollHeight = container.scrollHeight
            const prevScrollTop = container.scrollTop

            this.isLoading = true
            this.page += 1

            return new Api().getChatList({
                size: this.size,
                page: this.page,
                q: {userId: 171},
            }).then(res => {
                if (res.status === 200) {
                    const records = res.data.records || []

                    // 判断是否还有更多
                    const currentTotal = (this.page - 1) * this.size + this.messageList.length + records.length
                    this.hasMore = currentTotal < this.totalNumber

                    // 旧消息插入到前面
                    this.messageList = [...records, ...this.messageList]

                    // 恢复滚动位置，防止页面跳动
                    this.$nextTick(() => {
                        if (container) {
                            const newScrollHeight = container.scrollHeight
                            // 保持用户之前看到的内容位置不变
                            container.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight)
                        }
                    })
                }
                return res
            }).finally(() => {
                this.isLoading = false
            })
        },

        // 滚动到底部
        scrollToBottom() {
            const container = this.$refs.messagesContainer
            if (!container) return

            this.$nextTick(() => {
                // 使用 requestAnimationFrame 确保 DOM 已完全渲染
                requestAnimationFrame(() => {
                    container.scrollTop = container.scrollHeight
                })
            })
        }
    }
}
</script>

<style scoped>
/* 弹窗主体：覆盖 element-ui 默认样式 */
>>> .chat-modal-dialog {
    border-radius: 10px;
    overflow: hidden;
}

>>> .chat-modal-dialog .el-dialog__header {
    padding: 0;
    border-bottom: none;
}

>>> .chat-modal-dialog .el-dialog__body {
    padding: 0;
}

>>> .chat-modal-dialog .el-dialog__footer {
    padding: 0;
    border-top: 1px solid #eaeaea;
}

.chat-header-bar {
    padding: 20px;
    background: #3498db;
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.chat-header-info {
    display: flex;
    align-items: center;
    gap: 15px;
}

.chat-header-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: white;
    color: #3498db;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: 600;
}

.chat-header-text h3 {
    font-size: 18px;
    margin-bottom: 5px;
}

.chat-header-text p {
    font-size: 14px;
    opacity: 0.9;
}

.chat-close {
    background: none;
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s;
}

.chat-close:hover {
    background-color: rgba(255, 255, 255, 0.1);
}

.chat-messages {
    height: 480px;
    padding: 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.message-bubble {
    max-width: 70%;
    padding: 12px 16px;
    border-radius: 18px;
    position: relative;
    word-wrap: break-word;
}

.message-bubble.sent {
    align-self: flex-end;
    background-color: #3498db;
    color: white;
    border-bottom-right-radius: 4px;
}

.message-bubble.received {
    align-self: flex-start;
    background-color: #f1f1f1;
    color: #333;
    border-bottom-left-radius: 4px;
}

.message-time {
    font-size: 11px;
    opacity: 0.7;
    margin-top: 5px;
    text-align: right;
}

.chat-input-area {
    padding: 20px;
    display: flex;
    gap: 10px;
}

.chat-input {
    flex: 1;
    padding: 12px 16px;
    border: 1px solid #ddd;
    border-radius: 24px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;
}

.chat-input:focus {
    border-color: #3498db;
}

.send-btn {
    background-color: #3498db;
    color: white;
    border: none;
    border-radius: 50%;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s;
}

.send-btn:hover {
    background-color: #2980b9;
}

.send-btn i {
    font-size: 18px;
}

@media (max-width: 768px) {
    >>> .chat-modal-dialog {
        width: 95% !important;
    }

    .chat-messages {
        height: 60vh;
    }
}

.chat-loading {
    position: sticky;
    top: 0;
    text-align: center;
    color: #666;
    font-size: 12px;
    padding: 6px 0;
    background: rgba(255, 255, 255, 0.85);
}

.chat-loading i {
    margin-right: 6px;
}

/* 发送按钮禁用状态 */
.send-btn.disabled {
    background-color: #d0d0d0;
    cursor: not-allowed;
    opacity: 0.6;
}

.send-btn.disabled:hover {
    background-color: #d0d0d0;
}

/* 发送中状态 */
.send-btn.sending {
    background-color: #2980b9;
}

/* 发送动画 - 旋转飞出 */
@keyframes sendAnimation {
    0% {
        transform: translate(0, 0) rotate(0deg) scale(1);
        opacity: 1;
    }
    50% {
        transform: translate(10px, -10px) rotate(45deg) scale(1.2);
        opacity: 0.8;
    }
    100% {
        transform: translate(20px, -20px) rotate(90deg) scale(0.5);
        opacity: 0;
    }
}

.send-animation {
    animation: sendAnimation 0.3s ease-out;
}

/* 发送按钮过渡效果 */
.send-btn {
    transition: all 0.2s ease;
}

.send-btn i {
    transition: transform 0.2s ease;
}

/* 按钮按下效果 */
.send-btn:active:not(.disabled) {
    transform: scale(0.95);
}
</style>